import type { LoginTypeEnum } from '@app-store'

export interface SessionCrossTabApi {
  broadcastForceLogout: (loginType?: LoginTypeEnum) => void
  subscribeCrossTabLogout: (
    onLogout: (loginType?: LoginTypeEnum) => void
  ) => () => void
  broadcastSessionActivity: (ts: number) => void
  subscribeSessionActivityBroadcast: (
    onActivity: (ts: number) => void
  ) => () => void
  broadcastLoginSuccess: () => void
  broadcastMandatoryReloginAck: (sessionLoginAtMs: number) => void
  subscribeMandatoryReloginAck: (
    onAck: (sessionLoginAtMs: number) => void
  ) => () => void
  subscribeCrossTabLoginSuccess: (onLogin: () => void) => () => void
  broadcastSessionTimerPauseUpdated: () => void
  subscribeSessionTimerPauseUpdated: (onUpdate: () => void) => () => void
  broadcastMaxSessionExtension: (
    sessionLoginAtMs: number,
    extensionCount: number
  ) => void
  subscribeMaxSessionExtension: (
    onExtend: (payload: {
      sessionLoginAtMs: number
      extensionCount: number
    }) => void
  ) => () => void
  broadcastMandatorySoftDismiss: (
    sessionLoginAtMs: number,
    extensionCount: number
  ) => void
  subscribeMandatorySoftDismiss: (
    onDismiss: (payload: {
      sessionLoginAtMs: number
      extensionCount: number
    }) => void
  ) => () => void
}

function createSessionCrossTab(prefix: string): SessionCrossTabApi {
  const LOGOUT_CHANNEL = `${prefix}_force_logout`
  const LOGIN_CHANNEL = `${prefix}_login_success`
  const ACTIVITY_CHANNEL = `${prefix}_session_activity`
  const MANDATORY_RELOGIN_ACK_CHANNEL = `${prefix}_mandatory_relogin_ack`
  const SESSION_TIMER_PAUSE_CHANNEL = `${prefix}_session_timer_pause`
  const MAX_SESSION_EXT_CHANNEL = `${prefix}_max_session_extension`
  const MANDATORY_SOFT_DISMISS_CHANNEL = `${prefix}_mandatory_soft_dismiss`

  const LOGOUT_MSG = `${prefix}_logout`
  const ACTIVITY_MSG = `${prefix}_activity`
  const MANDATORY_ACK_MSG = `${prefix}_mandatory_relogin_ack`
  const LOGIN_SUCCESS_MSG = `${prefix}_login_success`
  const SESSION_TIMER_PAUSE_MSG = `${prefix}_session_timer_pause_updated`
  const MAX_SESSION_EXT_MSG = `${prefix}_max_session_extension`
  const MANDATORY_SOFT_DISMISS_MSG = `${prefix}_mandatory_soft_dismiss`

  interface LogoutMessage {
    type: string
    loginType?: LoginTypeEnum
  }

  interface ActivityMessage {
    type: string
    ts: number
  }

  interface MandatoryReloginAckMessage {
    type: string
    sessionLoginAtMs: number
  }

  interface SessionTimerPauseMessage {
    type: string
  }

  interface MaxSessionExtensionMessage {
    type: string
    sessionLoginAtMs: number
    extensionCount: number
  }

  interface MandatorySoftDismissMessage {
    type: string
    sessionLoginAtMs: number
    extensionCount: number
  }

  function broadcastForceLogout(loginType?: LoginTypeEnum): void {
    try {
      const ch = new BroadcastChannel(LOGOUT_CHANNEL)
      const msg: LogoutMessage = { type: LOGOUT_MSG }
      if (loginType !== undefined) msg.loginType = loginType
      ch.postMessage(msg)
      ch.close()
    } catch {
      /* ignore */
    }
  }

  function subscribeCrossTabLogout(
    onLogout: (loginType?: LoginTypeEnum) => void
  ): () => void {
    let ch: BroadcastChannel | undefined
    try {
      ch = new BroadcastChannel(LOGOUT_CHANNEL)
      ch.onmessage = (ev: MessageEvent) => {
        const data = ev.data as LogoutMessage
        if (data?.type === LOGOUT_MSG) {
          onLogout(data.loginType)
        }
      }
    } catch {
      /* ignore */
    }
    return () => {
      try {
        ch?.close()
      } catch {
        /* ignore */
      }
    }
  }

  function broadcastSessionActivity(ts: number): void {
    try {
      const ch = new BroadcastChannel(ACTIVITY_CHANNEL)
      ch.postMessage({ type: ACTIVITY_MSG, ts } satisfies ActivityMessage)
      ch.close()
    } catch {
      /* ignore */
    }
  }

  function subscribeSessionActivityBroadcast(
    onActivity: (ts: number) => void
  ): () => void {
    let ch: BroadcastChannel | undefined
    try {
      ch = new BroadcastChannel(ACTIVITY_CHANNEL)
      ch.onmessage = (ev: MessageEvent) => {
        const data = ev.data as ActivityMessage
        if (data?.type === ACTIVITY_MSG && typeof data.ts === 'number') {
          onActivity(data.ts)
        }
      }
    } catch {
      /* ignore */
    }
    return () => {
      try {
        ch?.close()
      } catch {
        /* ignore */
      }
    }
  }

  function broadcastLoginSuccess(): void {
    try {
      const ch = new BroadcastChannel(LOGIN_CHANNEL)
      ch.postMessage({ type: LOGIN_SUCCESS_MSG })
      ch.close()
    } catch {
      /* ignore */
    }
  }

  function broadcastMandatoryReloginAck(sessionLoginAtMs: number): void {
    try {
      const ch = new BroadcastChannel(MANDATORY_RELOGIN_ACK_CHANNEL)
      ch.postMessage({
        type: MANDATORY_ACK_MSG,
        sessionLoginAtMs,
      } satisfies MandatoryReloginAckMessage)
      ch.close()
    } catch {
      /* ignore */
    }
  }

  function subscribeMandatoryReloginAck(
    onAck: (sessionLoginAtMs: number) => void
  ): () => void {
    let ch: BroadcastChannel | undefined
    try {
      ch = new BroadcastChannel(MANDATORY_RELOGIN_ACK_CHANNEL)
      ch.onmessage = (ev: MessageEvent) => {
        const data = ev.data as MandatoryReloginAckMessage
        if (
          data?.type === MANDATORY_ACK_MSG &&
          typeof data.sessionLoginAtMs === 'number'
        ) {
          onAck(data.sessionLoginAtMs)
        }
      }
    } catch {
      /* ignore */
    }
    return () => {
      try {
        ch?.close()
      } catch {
        /* ignore */
      }
    }
  }

  function subscribeCrossTabLoginSuccess(onLogin: () => void): () => void {
    let ch: BroadcastChannel | undefined
    try {
      ch = new BroadcastChannel(LOGIN_CHANNEL)
      ch.onmessage = (ev: MessageEvent) => {
        const data = ev.data as { type?: string }
        if (data?.type === LOGIN_SUCCESS_MSG) {
          onLogin()
        }
      }
    } catch {
      /* ignore */
    }
    return () => {
      try {
        ch?.close()
      } catch {
        /* ignore */
      }
    }
  }

  function broadcastSessionTimerPauseUpdated(): void {
    try {
      const ch = new BroadcastChannel(SESSION_TIMER_PAUSE_CHANNEL)
      ch.postMessage({
        type: SESSION_TIMER_PAUSE_MSG,
      } satisfies SessionTimerPauseMessage)
      ch.close()
    } catch (err) {
      console.error(err)
    }
  }

  function subscribeSessionTimerPauseUpdated(onUpdate: () => void): () => void {
    let ch: BroadcastChannel | undefined
    try {
      ch = new BroadcastChannel(SESSION_TIMER_PAUSE_CHANNEL)
      ch.onmessage = (ev: MessageEvent) => {
        const data = ev.data as SessionTimerPauseMessage
        if (data?.type === SESSION_TIMER_PAUSE_MSG) {
          onUpdate()
        }
      }
    } catch (err) {
      console.error(err)
    }
    return () => {
      try {
        ch?.close()
      } catch (err) {
        console.error(err)
      }
    }
  }

  function broadcastMaxSessionExtension(
    sessionLoginAtMs: number,
    extensionCount: number
  ): void {
    try {
      const ch = new BroadcastChannel(MAX_SESSION_EXT_CHANNEL)
      ch.postMessage({
        type: MAX_SESSION_EXT_MSG,
        sessionLoginAtMs,
        extensionCount,
      } satisfies MaxSessionExtensionMessage)
      ch.close()
    } catch {
      /* ignore */
    }
  }

  function subscribeMaxSessionExtension(
    onExtend: (payload: {
      sessionLoginAtMs: number
      extensionCount: number
    }) => void
  ): () => void {
    let ch: BroadcastChannel | undefined
    try {
      ch = new BroadcastChannel(MAX_SESSION_EXT_CHANNEL)
      ch.onmessage = (ev: MessageEvent) => {
        const data = ev.data as MaxSessionExtensionMessage
        if (
          data?.type === MAX_SESSION_EXT_MSG &&
          typeof data.sessionLoginAtMs === 'number' &&
          typeof data.extensionCount === 'number'
        ) {
          onExtend({
            sessionLoginAtMs: data.sessionLoginAtMs,
            extensionCount: data.extensionCount,
          })
        }
      }
    } catch {
      /* ignore */
    }
    return () => {
      try {
        ch?.close()
      } catch {
        /* ignore */
      }
    }
  }

  function broadcastMandatorySoftDismiss(
    sessionLoginAtMs: number,
    extensionCount: number
  ): void {
    try {
      const ch = new BroadcastChannel(MANDATORY_SOFT_DISMISS_CHANNEL)
      ch.postMessage({
        type: MANDATORY_SOFT_DISMISS_MSG,
        sessionLoginAtMs,
        extensionCount,
      } satisfies MandatorySoftDismissMessage)
      ch.close()
    } catch {
      /* ignore */
    }
  }

  function subscribeMandatorySoftDismiss(
    onDismiss: (payload: {
      sessionLoginAtMs: number
      extensionCount: number
    }) => void
  ): () => void {
    let ch: BroadcastChannel | undefined
    try {
      ch = new BroadcastChannel(MANDATORY_SOFT_DISMISS_CHANNEL)
      ch.onmessage = (ev: MessageEvent) => {
        const data = ev.data as MandatorySoftDismissMessage
        if (
          data?.type === MANDATORY_SOFT_DISMISS_MSG &&
          typeof data.sessionLoginAtMs === 'number' &&
          typeof data.extensionCount === 'number'
        ) {
          onDismiss({
            sessionLoginAtMs: data.sessionLoginAtMs,
            extensionCount: data.extensionCount,
          })
        }
      }
    } catch {
      /* ignore */
    }
    return () => {
      try {
        ch?.close()
      } catch {
        /* ignore */
      }
    }
  }

  return {
    broadcastForceLogout,
    subscribeCrossTabLogout,
    broadcastSessionActivity,
    subscribeSessionActivityBroadcast,
    broadcastLoginSuccess,
    broadcastMandatoryReloginAck,
    subscribeMandatoryReloginAck,
    subscribeCrossTabLoginSuccess,
    broadcastSessionTimerPauseUpdated,
    subscribeSessionTimerPauseUpdated,
    broadcastMaxSessionExtension,
    subscribeMaxSessionExtension,
    broadcastMandatorySoftDismiss,
    subscribeMandatorySoftDismiss,
  }
}

let crossTabApi: SessionCrossTabApi | null = null

/**
 * Use a distinct prefix per product (e.g. `agent_desk`, `admin_desk`) so tabs on the
 * same origin do not cross-talk between apps.
 */
export function configureSessionCrossTab(prefix: string): void {
  crossTabApi = createSessionCrossTab(prefix)
}

export function getSessionCrossTab(): SessionCrossTabApi {
  if (!crossTabApi) {
    throw new Error(
      'configureSessionCrossTab must be called before cross-tab session helpers run'
    )
  }
  return crossTabApi
}

/** Re-export bound functions for ergonomic imports (same names as legacy app modules). */
export function broadcastForceLogout(loginType?: LoginTypeEnum): void {
  getSessionCrossTab().broadcastForceLogout(loginType)
}

export function subscribeCrossTabLogout(
  onLogout: (loginType?: LoginTypeEnum) => void
): () => void {
  return getSessionCrossTab().subscribeCrossTabLogout(onLogout)
}

export function broadcastSessionActivity(ts: number): void {
  getSessionCrossTab().broadcastSessionActivity(ts)
}

export function subscribeSessionActivityBroadcast(
  onActivity: (ts: number) => void
): () => void {
  return getSessionCrossTab().subscribeSessionActivityBroadcast(onActivity)
}

export function broadcastLoginSuccess(): void {
  getSessionCrossTab().broadcastLoginSuccess()
}

export function broadcastMandatoryReloginAck(sessionLoginAtMs: number): void {
  getSessionCrossTab().broadcastMandatoryReloginAck(sessionLoginAtMs)
}

export function subscribeMandatoryReloginAck(
  onAck: (sessionLoginAtMs: number) => void
): () => void {
  return getSessionCrossTab().subscribeMandatoryReloginAck(onAck)
}

export function subscribeCrossTabLoginSuccess(onLogin: () => void): () => void {
  return getSessionCrossTab().subscribeCrossTabLoginSuccess(onLogin)
}

export function broadcastSessionTimerPauseUpdated(): void {
  getSessionCrossTab().broadcastSessionTimerPauseUpdated()
}

export function subscribeSessionTimerPauseUpdated(
  onUpdate: () => void
): () => void {
  return getSessionCrossTab().subscribeSessionTimerPauseUpdated(onUpdate)
}

export function broadcastMaxSessionExtension(
  sessionLoginAtMs: number,
  extensionCount: number
): void {
  getSessionCrossTab().broadcastMaxSessionExtension(
    sessionLoginAtMs,
    extensionCount
  )
}

export function subscribeMaxSessionExtension(
  onExtend: (payload: {
    sessionLoginAtMs: number
    extensionCount: number
  }) => void
): () => void {
  return getSessionCrossTab().subscribeMaxSessionExtension(onExtend)
}

export function broadcastMandatorySoftDismiss(
  sessionLoginAtMs: number,
  extensionCount: number
): void {
  getSessionCrossTab().broadcastMandatorySoftDismiss(
    sessionLoginAtMs,
    extensionCount
  )
}

export function subscribeMandatorySoftDismiss(
  onDismiss: (payload: {
    sessionLoginAtMs: number
    extensionCount: number
  }) => void
): () => void {
  return getSessionCrossTab().subscribeMandatorySoftDismiss(onDismiss)
}
